import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, UserRound, BriefcaseBusiness } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { formatApiError, Spinner } from '../utils/helpers';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginDemo } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(lang === 'id' ? 'Berhasil masuk!' : 'Login successful!');
      navigate(user.role === 'staff' ? '/karyawan' : '/admin');
    } catch (err) {
      toast.error(formatApiError(err) || 'Login gagal');
    } finally {
      setLoading(false);
    }
  };
  const handleDemoLogin = async (role) => {
    setLoading(true);
    try {
      const user = await loginDemo(role);
      toast.success('Masuk ke workspace demo');
      navigate(user.role === 'staff' ? '/karyawan' : '/admin');
    } catch (err) {
      toast.error(formatApiError(err) || 'Login demo gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <SEO title="Masuk" description="Masuk ke akun KasirWA Anda untuk mengelola bisnis jasa." path="/login" noindex={true} />
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 max-w-lg mx-auto lg:mx-0 lg:max-w-md">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#0f6c44] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold font-manrope text-lg">K</span>
            </div>
            <span className="text-xl font-bold text-slate-800 font-manrope">KasirWA</span>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 font-manrope tracking-tight">
            {lang === 'id' ? 'Selamat Datang' : 'Welcome Back'}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            {lang === 'id' ? 'Masuk untuk melanjutkan ke dashboard' : 'Sign in to continue to your dashboard'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              data-testid="login-email-input" required autoFocus
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800 placeholder-slate-400"
              placeholder="nama@email.com" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700">Password</label>
              <a href="/forgot-password" className="text-xs text-[#0f6c44] hover:underline font-medium">Lupa Password?</a>
            </div>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                data-testid="login-password-input" required
                className="w-full px-4 py-3 pr-12 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] outline-none transition-all text-slate-800"
                placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full h-12 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? <Spinner size="sm" className="border-white/30 border-t-white" /> : <><LogIn size={18} />{lang === 'id' ? 'Masuk' : 'Sign In'}</>}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 mt-5">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400 font-medium">{lang === 'id' ? 'atau' : 'or'}</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Google Button */}
        <a href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/login')}`}
          data-testid="google-login-button"
          className="mt-4 w-full h-12 flex items-center justify-center gap-3 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all">
          <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          {lang === 'id' ? 'Masuk dengan Google' : 'Continue with Google'}
        </a>

        <p className="mt-6 text-center text-sm text-slate-500">
          {lang === 'id' ? 'Belum punya akun?' : "Don't have an account?"}{' '}
          <Link to="/register" className="text-[#0f6c44] font-semibold hover:underline">
            {lang === 'id' ? 'Daftar toko' : 'Register store'}
          </Link>
        </p>
        <button onClick={toggleLang} className="mt-3 text-xs text-slate-400 hover:text-slate-600 mx-auto block">
          {lang === 'id' ? '🇬🇧 Switch to English' : '🇮🇩 Ganti ke Indonesia'}
        </button>
        <div data-testid="demo-login-panel" className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold text-amber-800">{lang === 'id' ? 'Coba Langsung' : 'Try the Demo'}</p>
          <p className="mt-1 text-xs leading-relaxed text-amber-700">{lang === 'id' ? 'Masuk ke workspace simulasi tanpa memasukkan email atau password.' : 'Enter the simulated workspace without entering email or password.'}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => handleDemoLogin('owner')} disabled={loading} data-testid="demo-login-owner-button" className="flex items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-amber-600 disabled:opacity-60"><UserRound size={14} />Owner Demo</button>
            <button type="button" onClick={() => handleDemoLogin('staff')} disabled={loading} data-testid="demo-login-staff-button" className="flex items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-bold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-60"><BriefcaseBusiness size={14} />Staff Demo</button>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 relative">
        <img src="https://images.pexels.com/photos/12935066/pexels-photo-12935066.jpeg"
          alt="KasirWA" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0f6c44]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-bold font-manrope mb-3">Kelola Bisnis Jasa Lebih Mudah</h2>
          <p className="text-white/80 text-base leading-relaxed mb-6">
            POS modern untuk bisnis jasa — order, kasir, dan laporan dalam satu platform.
          </p>
          <div className="flex gap-3 flex-wrap">
            {['Kanban Board', 'Pelacak Order', 'Laporan Kas', 'PDF Receipt'].map(f => (
              <span key={f} className="bg-white/15 backdrop-blur rounded-lg px-3 py-1.5 text-xs font-medium">{f}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
